import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { apiErrorText } from "@/lib/apiError";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { confirmPasswordReset } from "@/api/auth";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, SectionLabel } from "@/components/ui/Card";
import { AuthLanguagePicker } from "@/components/AuthLanguagePicker";

export default function ResetPassword() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!token) {
    return (
      <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <AuthLanguagePicker />
        <div className="mb-8 text-center">
          <h1 className="font-serif text-[34px] font-medium tracking-tight">Mavinty</h1>
        </div>
        <Card className="space-y-3">
          <SectionLabel>{t("auth.reset_invalid_title")}</SectionLabel>
          <p className="text-[14px] text-navy-deep">
            {t("auth.reset_invalid_body")}
          </p>
          <div className="border-t border-cream-border pt-4 text-[13px]">
            <Link
              to="/forgot-password"
              className="font-medium text-amber-dark hover:underline"
            >
              {t("auth.reset_request_new")}
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 8) {
      setError(t("auth.reset_too_short"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.reset_mismatch"));
      return;
    }

    setLoading(true);
    try {
      await confirmPasswordReset(token, password);
      nav("/login?reset=success", { replace: true });
    } catch (err) {
      setError(apiErrorText(err, t("auth.reset_failed")));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-dvh max-w-md flex-col justify-center px-5 py-10">
      <div className="mb-8 text-center">
        <h1 className="font-serif text-[34px] font-medium tracking-tight">Mavinty</h1>
      </div>
      <Card className="space-y-4">
        <div>
          <h2 className="font-serif text-[20px] font-medium text-navy-deep">
            {t("auth.reset_title")}
          </h2>
          <p className="mt-1 text-[13px] text-taupe">
            {t("auth.reset_lede")}
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <Input
            type="password"
            label={t("auth.reset_field_new")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={8}
            required
            autoComplete="new-password"
          />
          <Input
            type="password"
            label={t("auth.reset_field_repeat")}
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            minLength={8}
            required
            autoComplete="new-password"
          />
          {error ? (
            <div className="rounded-lg bg-coral/10 px-3 py-2 text-[13px] text-coral">
              {error}
            </div>
          ) : null}
          <Button type="submit" size="lg" className="w-full" loading={loading}>
            {t("auth.reset_submit")}
          </Button>
        </form>
      </Card>
    </div>
  );
}
